import { StrictMode, Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import { TooltipProvider } from './components/ui';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App } from './App';
import { ThemeProvider } from './components/theme';
import './styles.css';

/**
 * The web client, loaded only on its own path.
 *
 * It pulls in the SDK and with it a whole SIP stack. An admin who never opens it should not pay
 * for it, and — as happened once already — a module-level failure inside the SIP stack must break
 * one app rather than blanking both.
 */
const ClientApp = lazy(() => import('./client/ClientApp').then((m) => ({ default: m.ClientApp })));

/**
 * Which app to mount, decided from the path once at startup.
 *
 * Two apps in one bundle rather than two builds: they share the design system and the primitives,
 * and a second Vite target would double the build for a few hundred lines. They share no session
 * and no API client, which is the part that matters.
 */
const isWebClient = window.location.pathname.startsWith('/client');

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A PBX admin panel is usually one tab left open all day. Refetching on focus keeps trunk
      // and registration state honest without a polling loop on every screen.
      refetchOnWindowFocus: true,
      staleTime: 5_000,
    },
  },
});

const root = document.getElementById('root');
if (!root) throw new Error('#root is missing from index.html');

createRoot(root).render(
  <StrictMode>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        {/* One provider for the whole app: Radix shares the open/close timing across tooltips, so
            moving between two of them shows the second immediately instead of waiting again. */}
        <TooltipProvider>
          {isWebClient ? (
            <Suspense fallback={null}>
              <ClientApp />
            </Suspense>
          ) : (
            <App />
          )}
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>,
);
